import React, { useState } from 'react';
import { Button, CircularProgress, Box, Typography } from '@mui/material';
import { PiFolderOpen } from 'react-icons/pi';
import { IManageDocumentPicker } from './IManageDocumentPicker';
import { generatePrefixedJobName } from '../../utils/jobNaming';

interface IManageDocument {
  id: string;
  name: string;
  type: string;
  extension: string;
  size: number;
  created: string;
  modified: string;
  author: string;
  version: string;
}

interface IManageFileSelectorProps {
  onFilesSelected: (files: File[]) => void;
  disabled?: boolean;
  variant?: 'contained' | 'outlined' | 'text';
  size?: 'small' | 'medium' | 'large';
  fullWidth?: boolean;
  practice?: string;
  jobNumber?: string;
}

export const IManageFileSelector: React.FC<IManageFileSelectorProps> = ({
  onFilesSelected,
  disabled = false,
  variant = 'outlined',
  size = 'medium',
  fullWidth = false,
  practice,
  jobNumber
}) => {
  const [open, setOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedDocuments, setSelectedDocuments] = useState<IManageDocument[]>([]);

  const handleDocumentsSelected = async (documents: IManageDocument[], workspaceId: string, folderId: string) => {
    setDownloading(true);
    setError(null);
    const processedFiles: File[] = [];

    console.log('Downloading documents:', {
      documents: documents.map(d => ({ id: d.id, name: d.name })),
      workspaceId,
      folderId
    });

    try {
      for (const document of documents) {
        // Ensure proper file extension for Step Functions processing
        const properFileName = document.name.includes('.') ? document.name : `${document.name}.pdf`;
        
        console.log('Downloading document to S3:', {
          documentId: document.id,
          workspaceId,
          folderId,
          documentName: document.name,
          properFileName
        });
        
        // Generate S3 key for the document
        const jobName = jobNumber || generatePrefixedJobName('imanage');
        const timestamp = Date.now();
        const s3Key = `mister-fusion/dev/${practice || 'general'}/${jobName}/${timestamp}-${properFileName}`;
        const s3Bucket = process.env.REACT_APP_S3_BUCKET || 'mrfusion-dev-843339892914';
        
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/imanage/download-to-s3`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token') || sessionStorage.getItem('token')}`,
          },
          body: JSON.stringify({
            documentId: document.id,
            workspaceId,
            folderId,
            s3Bucket,
            s3Key
          }),
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error('S3 upload error:', response.status, errorText);
          throw new Error(`Failed to upload ${document.name} to S3: ${response.status} - ${errorText.substring(0, 200)}`);
        }
        
        const uploadResult = await response.json();
        if (!uploadResult.success) {
          throw new Error(`S3 upload failed: ${uploadResult.error}`);
        }
        
        console.log('Document uploaded to S3:', uploadResult.s3Uri);
        
        // Create a File object that represents the S3 file
        // Use a small placeholder blob since the actual content is in S3
        const placeholder = new Blob([`S3 file: ${uploadResult.s3Uri}`], { type: 'application/pdf' });

        const fileWithS3 = new File([placeholder], properFileName, {
          type: 'application/pdf',
          lastModified: new Date(document.modified).getTime()
        });

        // Mark this file as already uploaded to S3
        (fileWithS3 as any).s3Uri = uploadResult.s3Uri;
        (fileWithS3 as any).isS3File = true;
        (fileWithS3 as any).originalSize = uploadResult.size || document.size;
        processedFiles.push(fileWithS3);
      }

      onFilesSelected(processedFiles);
      setOpen(false);
      setSelectedDocuments([]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to process files');
    } finally {
      setDownloading(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedDocuments([]);
    setError(null);
  };

  if (downloading) {
    return (
      <Box sx={{ textAlign: 'center', p: 2 }}>
        <CircularProgress />
        <Typography variant="body2" sx={{ mt: 1 }}>Processing files...</Typography>
      </Box>
    );
  }

  return (
    <>
      <Button
        variant={variant}
        size={size}
        fullWidth={fullWidth}
        disabled={disabled}
        startIcon={<PiFolderOpen size={18} />}
        onClick={() => setOpen(true)}
        sx={{
          borderStyle: 'dashed',
          borderWidth: 2,
          '&:hover': { borderStyle: 'solid' }
        }}
      >
        Select from iManage
      </Button>

      {error && (
        <Typography color="error" variant="body2" sx={{ mt: 1 }}>
          {error}
        </Typography>
      )}

      <IManageDocumentPicker
        isOpen={open}
        onClose={handleClose}
        onDocumentsSelected={handleDocumentsSelected}
        selectedDocuments={selectedDocuments}
      />
    </>
  );
};